/**
 * FIRST OPEN, piece 3: the last known home shell (packet J12).
 *
 * Bobbie, 2026-09-07: "Many people are using the app and the initial upload
 * right now. It takes way way way too long." A cold open shows a full-page
 * spinner on an otherwise empty screen while the two boot checks run (they
 * run in parallel already, see App.tsx). Nothing about which home is coming
 * changes between one visit and the next on the same phone, so the shell of
 * that home can be on screen from the first frame with the spinner INSIDE
 * it, instead of a page with nothing on it.
 *
 * WHAT IS REMEMBERED, and nothing else: the home KIND (staff, family or
 * dancer) and a FIRST NAME. That is the whole record. No role, no uid, no
 * class, no message, no count, nothing a person could read as studio data.
 * The shell is a frame and a name; every real thing on the home page still
 * comes from the live answer, and the moment the real answer lands it
 * replaces this, matching or not.
 *
 * WHAT IS NEVER TRUSTED: this is a hint, exactly like dancerSessionHint.ts,
 * and it opens no door. It cannot grant a route, a role, or a read; it only
 * decides what shape of placeholder is on screen for a few hundred
 * milliseconds. A wrong value costs a flash of the wrong frame, which is
 * why it is safe to paint before the checks answer, and why every read
 * validates rather than casts.
 *
 * WHEN IT IS CLEARED: every sign-out (lib/auth.ts for an adult, the dancer
 * logout and the stale-cookie eviction in dancer-session-context.tsx), so
 * the next person on a shared phone never sees the last person's name.
 */

const LAST_HOME_SHELL_KEY = 'ldg-hub-last-home-shell';

/** The three homes Landing.tsx can resolve to. */
export type HomeShellKind = 'staff' | 'family' | 'dancer';

const HOME_SHELL_KINDS: readonly HomeShellKind[] = ['staff', 'family', 'dancer'];

export interface HomeShell {
  kind: HomeShellKind;
  /** May be empty: a child-only dancer session has no Firebase display name. */
  firstName: string;
}

/**
 * The label each home's OWN loading state already uses, so the shell says
 * what the page it is standing in for would say. Nothing new is written
 * here: 'Getting your dance day ready...' and 'Setting up your home' are
 * both lifted from Landing.tsx's existing Suspense fallbacks.
 */
export const HOME_SHELL_LABEL: Record<HomeShellKind, string> = {
  staff: 'Setting up your home',
  family: 'Setting up your home',
  dancer: 'Getting your dance day ready...',
};

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage ?? null;
  } catch {
    // Private browsing, disabled storage, or a locked-down webview can throw
    // on ACCESS to window.localStorage itself, not just on a call: same
    // defensive shape as dancerSessionHint.ts and previewRole.ts.
    return null;
  }
}

/**
 * Characters that are never part of a name: control characters, digits, and
 * the punctuation and symbols that only ever show up in markup or in junk.
 * Written as a DENY list of explicit ranges rather than an allow list of
 * letters, for two reasons. A \p{L} allow list needs a Unicode property
 * escape, and scripts/check-regex-floor rejects those outright because they
 * stop an older Safari chunk from parsing. And an allow list spelled out as
 * ranges instead would quietly erase whole scripts: a name in Arabic or
 * Japanese would come back empty and the shell would show a grey block for
 * a person whose name the app knows perfectly well. This keeps letters,
 * marks and accents of every script, plus the apostrophe, hyphen and period
 * that real names carry.
 */
const NOT_PART_OF_A_NAME = /[\u0000-\u001F\u007F-\u009F0-9<>&"`|/\\@#$%^*()_+=~{}[\];:?!,]/g;

/**
 * The first word of a display name, bounded and stripped down to the
 * characters a name is made of. Storage is same-origin and only this device
 * can write it, but this value goes straight into the DOM before any check
 * has answered, so it is narrowed on the way OUT as well as on the way in.
 */
export function firstNameFrom(displayName: string | null | undefined): string {
  if (typeof displayName !== 'string') return '';
  const first = displayName.trim().split(/\s+/)[0] ?? '';
  return first.replace(NOT_PART_OF_A_NAME, '').slice(0, 24);
}

/**
 * Which home a resolved boot landed on, or null when there is nothing worth
 * remembering. Mirrors Landing.tsx exactly: a dancer cookie wins, then an
 * ACTIVE family account, then an active staff account. A pending, rejected
 * or inactive account, and a signed-out visitor, all remember nothing,
 * because none of them lands on a home this shell would be standing in for.
 */
export function homeShellKindFor(params: {
  dancerAuthenticated: boolean;
  signedIn: boolean;
  role: string | null | undefined;
  status: string | null | undefined;
}): HomeShellKind | null {
  if (params.dancerAuthenticated) return 'dancer';
  if (!params.signedIn) return null;
  if (params.status !== 'active') return null;
  if (params.role === 'family' || params.role === 'foh') return 'family';
  if (
    params.role === 'director'
    || params.role === 'manager'
    || params.role === 'company'
    || params.role === 'ensemble'
  ) {
    return 'staff';
  }
  return null;
}

/**
 * Read the shell back. Anything unexpected reads as "nothing remembered",
 * which is the same path a first-ever visit takes: the plain spinner,
 * exactly as before this packet.
 */
export function readLastHomeShell(): HomeShell | null {
  const store = storage();
  if (!store) return null;
  let raw: string | null = null;
  try {
    raw = store.getItem(LAST_HOME_SHELL_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const record = parsed as { kind?: unknown; firstName?: unknown };
    const kind = HOME_SHELL_KINDS.find((k) => k === record.kind);
    if (!kind) return null;
    return {
      kind,
      firstName: firstNameFrom(typeof record.firstName === 'string' ? record.firstName : ''),
    };
  } catch {
    // Not JSON, or JSON of a shape nobody here wrote. Forget it rather than
    // paint something out of it.
    return null;
  }
}

/**
 * Record a resolved home. An empty first name KEEPS the name already stored
 * for the same kind rather than blanking it: a child-only dancer session has
 * no Firebase display name to offer, and the dancer's own home writes the
 * real one from its portal payload a moment later.
 */
export function rememberHomeShell(shell: { kind: HomeShellKind; firstName?: string | null }): void {
  const store = storage();
  if (!store) return;
  const firstName = firstNameFrom(shell.firstName);
  const previous = readLastHomeShell();
  const kept = !firstName && previous && previous.kind === shell.kind ? previous.firstName : firstName;
  if (previous && previous.kind === shell.kind && previous.firstName === kept) return;
  try {
    store.setItem(LAST_HOME_SHELL_KEY, JSON.stringify({ kind: shell.kind, firstName: kept }));
  } catch {
    // Storage full or blocked mid-write: the shell just will not persist,
    // the same fallback every other localStorage write in this app takes.
  }
}

/**
 * Forget the shell ONLY when it is of this kind.
 *
 * Codex review of PR #1099, P1. The dancer cookie check answers "no dancer
 * session" on three different paths, and only one of them used to forget
 * the shell: an ADULT evicting a stale cookie. A child-only dancer session
 * whose cookie simply expired has no adult account behind it at all, so it
 * fell straight through to the ordinary signed-out answer and left the
 * kid's first name stored. Every later cold open of the signed-out app on
 * that device then painted a child's name, on a phone that is very often
 * shared. Every one of those paths now calls this.
 *
 * KIND SCOPED, and that is the whole point of the function. "No dancer
 * cookie on this device" is a real answer about a DANCER session and about
 * nothing else. It is also the answer an ordinary adult gets on every
 * single boot, so an unconditional clear here would wipe a staff or family
 * shell every time the app started and the feature would never work for
 * anyone. A dancer shell is the only thing this answer contradicts.
 */
export function forgetHomeShellKind(kind: HomeShellKind): void {
  const current = readLastHomeShell();
  if (!current || current.kind !== kind) return;
  clearLastHomeShell();
}

/** Forget the shell. Called on every sign-out, adult and dancer. */
export function clearLastHomeShell(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(LAST_HOME_SHELL_KEY);
  } catch {
    // ignore
  }
}
